import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, frames } from "../../Styles";
import { getLabelFromKey, ReportData, ReportTemplate } from "../../Definitions";
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";
import { getKeyFromDataLevel } from "../../Definitions/valueTypes";

interface ReportCard_Props {
  report?: ReportData
  template?: ReportTemplate
  onCardSelect?: (id: number) => void
}

export default function ReportCard({report, template, onCardSelect}: ReportCard_Props) {
  const {t} = useTranslation()

  // #region | Report Data
  const data = useMemo(() => {
    const reportTitle = report ? report.name : template ? template.name : "";

    const reportSubTitle = report
      ? `${t("reportPages.archive.archivedReport", { defaultValue: "Archived Report" })} ${report.reportId}`
      : template ? `${t("reportPages.reports.template", { defaultValue: "Template" })} ${template.templateId}`
      : "";

    const _dataLevel = report ? report.dataLevel : template ? template.dataLevel : undefined 
    
    const dataLevel = _dataLevel ? t(`params.${getLabelFromKey(getKeyFromDataLevel(_dataLevel))}`) : t("reportPages.reports.noDataParameterFound", { defaultValue: "No data parameter found" });
    const startDate = report&&report.startDate ? new LocaleFarmDate(report.startDate * 1000).toDateForDisplay() : t(".reportPages.archive.noStartDateFound", { defaultValue: "No Start Date Found" })
    const endDate = report&&report.endDate ? new LocaleFarmDate(report.endDate * 1000).toDateForDisplay() : t("reportPages.archive.noEndDateFound", { defaultValue: "No End Date Found" })

    return {
      reportTitle,
      reportSubTitle,
      dataLevel,
      startDate,
      endDate
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, template, t])
  // #endregion

  // #region | Filter Parameters Content
  const filterParams = useMemo(() => {
    const _filterParams = report&&report.filterParameter && (report.filterParameter.length > 0)
      ? report.filterParameter
      : template&&template.filterParameter && (template.filterParameter.length > 0) ? template.filterParameter
        : t("reportPages.reports.noFilterParameterFound", { defaultValue: "No filter parameter found" });
    
      const hasAnimalFilterParam:boolean = Array.isArray(_filterParams)
      ? _filterParams.some(param =>
          param.some(paramKey => paramKey.key === "animal_number")
        )
      : false;
    
    return (
      <div>
        {Array.isArray(_filterParams) ? _filterParams.flatMap((param, i) =>
          param.map((paramKey, j) => (
            <div key={`${i}-${j}`} style={{ display: "grid", gridAutoColumns: "80px 1fr", gridAutoRows: "1fr", gap: "5px",...fonts.paragraph, ...fonts.fontSize4 , color: colors.greyDarkPrimary}}>
              <div style={{ gridArea: "1/1/2/2", ...fonts.fontWeight2 }}>{`${t(`params.${getLabelFromKey(paramKey.key)}`)}:`}</div>
              {hasAnimalFilterParam ? <div style={{width: "165px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", gridArea: "1/2/2/3"}}>{paramKey.value.map(({ label }) => label).join(", ")}</div>
              : <div style={{ gridArea: "1/2/2/3" }}>{paramKey.value.map(({ label }) => label).join(", ")}</div>}
            </div>
          ))
        ) : null}
      </div>
    )
  }, [report, template, t])


  // #endregion
  
  // #region | Data Parameters Content
  // const dataParams = useMemo(() => {
  //   const _dataParams = activeReport && activeReport.dataParameter && (activeReport.dataParameter.length > 0)
  //     ? activeReport.dataParameter
  //     : activeTemplate && activeTemplate.dataParameter && (activeTemplate.dataParameter.length > 0) ? activeTemplate.dataParameter
  //   : t("reportPages.reports.noDataParameterFound", { defaultValue: "No data parameter found" });

  //   return (
  //     <div style={{ }}>
  //       {Array.isArray(_dataParams) ? _dataParams.map((param, index) => (
  //         <p key={index} style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
  //           {t(`params.${getLabelFromKey(param)}`)}
  //         </p>
  //       )) : (
  //         <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
  //           {_dataParams}
  //         </p>
  //       )}
  //     </div>
  //   )
  // }, [activeReport, activeTemplate, t])
  // #endregion

  return (
    <frames.ReportCard
      onClick={onCardSelect ? () =>
        report ? onCardSelect(report?.reportId ?? 0)
          : template ? onCardSelect(template?.templateId ?? 0) : undefined
        : undefined}
    >

      <div style={{backgroundColor: colors.greyPrimary, minHeight: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", padding: "10px 20px", textAlign: "center" }}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, pointerEvents: "none"}}>{data.reportTitle}</p>
        <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, pointerEvents: "none"}}>{data.reportSubTitle}</p>
      </div>

      {/* Date Span */}
      { report ?
        <div style={{ width: "100%", pointerEvents: "none" }}>
          <div style={{ backgroundColor: colors.greyLightSecondary, height: "30px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {data.startDate === undefined && data.endDate === undefined ? <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}>{`undefined date`}</p>
              : <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}>{`${data.startDate} - ${data.endDate}`}</p> }
          </div>
        </div>
      : <></> }

      <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 5px 5px", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "20px", gap: "20px", pointerEvents: "none" }}>

        {/* Data Level */} 
        <Content
          header={t("reportPages.reports.dataLevel", { defaultValue: "Data Level" })}
          icon="DataLevel"
          content={<p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center"}}>{data.dataLevel}</p>}
        />

        {/* Filter Parameter */}
        <Content
          header={t("reportPages.reports.filterParameter", {defaultValue: "Filter Parameter"})}
          icon="FilterParameter"
          content={filterParams}
        />
        
        {/* Data Parameter */}
        {/* <Content
          header={t("reportPages.reports.dataParameter", {defaultValue: "Data Parameter"})}
          icon="DataParameter"
          content={dataParams}
        /> */}
        
      </div>

    </frames.ReportCard>
  )
}

const Content = memo(({ header, icon, content }: { header: string, icon: keyof typeof glyphs, content: JSX.Element}) => {
  const IconComponent = glyphs[icon];

  return (
    <div style={{ width: "100%", pointerEvents: "none" }}>
      <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
        {IconComponent && <IconComponent size="25px" fillColor={colors.greyDarkPrimary}/>}
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{header}</p>
      </div>
      <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
        {content}
      </div>
    </div>
  );
})
